import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { hasRole, type OrganizationType } from './types';
import { Loading } from '@/components/Loading';
import { EmptyState } from '@/components/EmptyState';
import { ShieldAlert } from 'lucide-react';

interface RequireAuthProps {
  /** 許可する組織区分。省略時はログイン済みなら誰でも可 */
  roles?: readonly OrganizationType[];
  children: ReactNode;
}

/**
 * 認証・認可ガード。
 * - 未ログイン → /login?redirect=現在の URL
 * - ロール不一致 → 権限なし表示
 */
export function RequireAuth({ roles, children }: RequireAuthProps) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <Loading fullscreen label="ログイン状態を確認しています" />;
  if (!user) {
    const redirect = location.pathname + location.search;
    const to = redirect && redirect !== '/' ? `/login?redirect=${encodeURIComponent(redirect)}` : '/login';
    return <Navigate to={to} replace />;
  }
  if (!hasRole(user, roles)) {
    return (
      <EmptyState
        icon={<ShieldAlert />}
        title="この画面を表示する権限がありません"
        description="ご利用の組織区分では、この機能を利用できません。必要な場合は振興基金の管理者へお問い合わせください。"
      />
    );
  }
  return <>{children}</>;
}
